import { useState, useEffect } from "react";
import { ArrowRight, Sparkles } from "lucide-react";
import { SIGNUP_URL } from "@/components/landing/constants";

function Sparkline() {
  const pts = [54, 58, 56, 63, 66, 70, 74];
  const max = 100;
  const w = 220;
  const h = 56;
  const step = w / (pts.length - 1);
  const path = pts
    .map((v, i) => `${i === 0 ? "M" : "L"}${(i * step).toFixed(1)},${(h - (v / max) * h).toFixed(1)}`)
    .join(" ");
  return (
    <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} className="block">
      <defs>
        <linearGradient id="sl" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#7C5CFC" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#7C5CFC" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`${path} L${w},${h} L0,${h} Z`} fill="url(#sl)" />
      <path d={path} fill="none" stroke="#A855F7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function MiqHeroCard() {
  const [score, setScore] = useState(62);
  useEffect(() => {
    const target = 74;
    let raf;
    const step = () => {
      setScore((s) => {
        if (s >= target) return target;
        const next = Math.min(target, s + 1);
        raf = requestAnimationFrame(step);
        return next;
      });
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div data-testid="hero-miq-card" className="glass-strong relative rounded-[28px] p-7 sm:p-8">
      <div className="flex items-center justify-between mb-6">
        <div className="text-[11px] uppercase tracking-[0.22em] text-white/55 font-semibold">Your MIQ — Today</div>
        <div className="flex items-center gap-2 text-[11px] text-brand-free font-semibold">
          <span className="h-1.5 w-1.5 rounded-full bg-brand-free animate-glow-pulse" />
          Live
        </div>
      </div>

      <div className="flex items-center gap-7">
        <div className="relative w-[160px] h-[160px] shrink-0">
          <div className="absolute inset-0 rounded-full conic-ring" />
          <div className="absolute inset-[6px] rounded-full bg-[#0A0A0A] flex flex-col items-center justify-center">
            <div data-testid="miq-score" className="font-mono text-5xl font-bold leading-none">{score}</div>
            <div className="mt-1 text-[10px] uppercase tracking-[0.2em] text-white/50">/ 100</div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-[0.18em] text-white/45 mb-2">Trend · 7 days</div>
          <Sparkline />
          <div className="mt-3 text-sm text-white/75">
            <span className="text-brand-free font-semibold">+8 pts</span> since Monday
          </div>
        </div>
      </div>

      <div className="mt-7 grid grid-cols-3 gap-3">
        {[
          { label: "Nutrition", val: 82, color: "bg-brand-free" },
          { label: "Recovery", val: 71, color: "bg-brand-smart" },
          { label: "Training", val: 68, color: "bg-brand-genius" },
        ].map((m) => (
          <div key={m.label} className="rounded-2xl bg-white/[0.03] border border-white/5 p-3">
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 font-semibold">{m.label}</div>
            <div className="mt-1 font-mono text-2xl font-bold">{m.val}</div>
            <div className="mt-2 h-1 rounded-full bg-white/5 overflow-hidden">
              <div className={`h-full ${m.color}`} style={{ width: `${m.val}%` }} />
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 rounded-2xl border border-white/5 bg-white/[0.02] p-4">
        <div className="flex items-start gap-3">
          <Sparkles className="w-4 h-4 text-brand-accent mt-0.5 shrink-0" />
          <div className="text-sm text-white/80 leading-relaxed">
            <span className="font-semibold text-white">Smart Pick:</span> Move protein earlier today —
            sleep was 6h12, your MIQ recovers faster with 35g protein before 11am.
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative bg-aurora bg-grain min-h-screen flex items-center pt-32 pb-24 overflow-hidden"
    >
      <div className="pointer-events-none absolute -right-32 top-24 w-[520px] h-[520px] opacity-30 blur-3xl">
        <div className="conic-ring w-full h-full rounded-full" />
      </div>
      <div className="pointer-events-none absolute -left-40 bottom-0 w-[420px] h-[420px] opacity-20 blur-3xl">
        <div className="rounded-full bg-brand-free w-full h-full" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 w-full">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div
              data-testid="hero-eyebrow"
              className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-white/80 font-semibold mb-8"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-brand-free animate-glow-pulse" />
              Personal Health Intelligence · MIQ 1.0
            </div>

            <h1
              data-testid="hero-headline"
              className="font-display font-black tracking-tighter text-5xl sm:text-6xl lg:text-7xl leading-[0.98]"
            >
              <span className="block text-gradient-hero">Your goal is personal.</span>
              <span className="block text-gradient-hero">Your intelligence should be too.</span>
            </h1>

            <p
              data-testid="hero-subheadline"
              className="mt-7 text-base sm:text-lg text-white/65 max-w-2xl leading-relaxed font-light"
            >
              Lose weight. Reverse pre-diabetes. Hit a marathon PR. Stack peptides for longevity.
              Track recovery from injury. SmartPlaite runs one AI brain on every signal — meals,
              training, sleep, supplements, bloodwork, biometrics — and turns it into your{" "}
              <span className="text-white font-medium">MIQ score</span>. The most personalized
              health platform built.
            </p>

            <div className="mt-9 flex flex-col sm:flex-row gap-3 sm:items-center">
              <a
                href={SIGNUP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-primary-cta"
                className="pill group inline-flex items-center justify-center gap-2 bg-white text-black px-7 py-4 font-semibold text-base hover:bg-white/90 shadow-[0_8px_32px_rgba(124,92,252,0.35)]"
              >
                Start 7 days free
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
              </a>
              <a
                href="#miq"
                data-testid="hero-secondary-cta"
                className="pill inline-flex items-center justify-center gap-2 glass px-6 py-4 text-white/90 font-medium text-base hover:bg-white/10"
              >
                See how MIQ works
              </a>
            </div>

            <div data-testid="hero-trust-microline" className="mt-6 text-xs text-white/45 max-w-xl space-y-1">
              <p>
                No credit card · Cancel anytime ·{" "}
                <span className="text-brand-accent font-semibold">
                  ★ Sign up with Google = 7 days Genius free
                </span>
              </p>
              <p>Built for Apple Health, Google Fit, Whoop, Oura, Garmin, Fitbit, Samsung</p>
            </div>
          </div>

          <div className="lg:col-span-5">
            <MiqHeroCard />
          </div>
        </div>
      </div>
    </section>
  );
}
